import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { map } from 'rxjs';
import { HdChartSettingsStore } from '@/app/human-design/chart/bodygraph/chart-tabs/hd-chart-settings.store';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '@/app/human-design/chart/bodygraph/hd-content-language.service';
import { IChingLinePosition } from '@/app/human-design/iching/core/iching.models';
import { HdIChingConsultComponent } from '@/app/human-design/iching/consult/hd-iching-consult.component';
import { HdIChingAccessService } from '@/app/human-design/iching/hd-iching-access.service';

/**
 * Classic I Ching-pagina (spec 2026-10-18): dunne route `/human-design/classic-iching`
 * rond de raadpleegcomponent. Werkt zonder geladen chart; toont een melding
 * wanneer de feature ontbreekt of de setting uit staat (met een knop die de
 * setting meteen aanzet). Queryparameters gaan eenmalig als `initial` naar de
 * raadpleging en worden daarna gewist, zodat een herlaad het concept niet
 * opnieuw overschrijft.
 */
@Component({
  selector: 'hd-classic-iching-page',
  standalone: true,
  imports: [HdIChingConsultComponent],
  templateUrl: './hd-classic-iching-page.component.html',
  styleUrl: './hd-classic-iching-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdClassicIChingPageComponent {
  protected readonly access = inject(HdIChingAccessService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly settings = inject(HdChartSettingsStore);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  /** Directe modus via `?hexagram=N&moving=2,5`. */
  protected readonly initial = toSignal(
    this.route.queryParamMap.pipe(
      map(params => {
        const hexagram = Number(params.get('hexagram'));
        if (!(hexagram >= 1 && hexagram <= 64)) {
          return undefined;
        }
        const moving = (params.get('moving') ?? '')
          .split(',')
          .map(Number)
          .filter(n => n >= 1 && n <= 6) as IChingLinePosition[];
        return { hexagram, moving };
      }),
    ),
  );

  private readonly lang = computed(() => {
    this.contentLang.translationsLoadedTick();
    return this.contentLang.effectiveLang();
  }, HD_LANG_RECOMPUTE_ON_LOAD);

  protected enable(): void {
    if (!this.settings.classicIChingEnabled()) {
      this.settings.toggleClassicIChing();
    }
  }

  /**
   * De raadpleging nam `initial` over: `hexagram` en `moving` weg, zonder extra
   * geschiedenisstap. De overige parameters (o.a. `navId`, waar de
   * AuthorizationGuard bij een herlaadbeurt op leunt) blijven staan.
   */
  protected clearQueryParams(): void {
    void this.router.navigate([], {
      queryParams: { hexagram: null, moving: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
      relativeTo: this.route,
    });
  }

  protected t(key: string): string {
    return this.transloco.translate<string>(key, undefined, this.lang());
  }
}
